import { KeyRound, MoreVertical, Pencil, Plus, Trash } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { IconButton } from "src/components/ui/icon-button";
import DatadogIcon from "src/components/ui/icons/datadog";
import { MarkdownEditorLazy as MarkdownEditor } from "src/components/ui/markdown-editor-lazy";
import { ErrorAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { DatadogIntegrationsDialogDeleteIntegrationMutation } from "./__generated__/DatadogIntegrationsDialogDeleteIntegrationMutation.graphql";
import type { DatadogIntegrationsDialogQuery } from "./__generated__/DatadogIntegrationsDialogQuery.graphql";
import type { DatadogIntegrationsDialogUpdateDescriptionMutation } from "./__generated__/DatadogIntegrationsDialogUpdateDescriptionMutation.graphql";
import DatadogIntegration from "./DatadogIntegration";
import DatadogIntegrationForm from "./DatadogIntegrationForm";

interface DatadogIntegrationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const datadogIntegrationsDialogQuery = graphql`
  query DatadogIntegrationsDialogQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: DATADOG) {
          edges {
            node {
              id
              name
              description
              integrationType
              integrationStatus
              credentials
              createdAt
            }
          }
        }
      }
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation DatadogIntegrationsDialogDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

const updateDescriptionMutation = graphql`
  mutation DatadogIntegrationsDialogUpdateDescriptionMutation($input: UpdateIntegrationDescriptionInput!) {
    updateIntegrationDescription(input: $input) {
      integration {
        id
        description
      }
      error
    }
  }
`;

export default function DatadogIntegrationsDialog({ isOpen, onClose, onRefetch, brandTheme }: DatadogIntegrationsDialogProps) {
  const [isConnectingNew, setIsConnectingNew] = useState(false);
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const [editingDescriptorDialog, setEditingDescriptorDialog] = useState<{
    isOpen: boolean;
    integrationId: string | null;
    integrationName: string | null;
    currentValue: string;
  }>({
    isOpen: false,
    integrationId: null,
    integrationName: null,
    currentValue: "",
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    integration: any | null;
  }>({
    isOpen: false,
    integration: null,
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingDescriptor, setIsSavingDescriptor] = useState(false);
  const [editingKeysDialog, setEditingKeysDialog] = useState<{
    isOpen: boolean;
    integrationId: string;
    integrationName: string;
    datadogSite: string;
    redactedApiKey: string;
    redactedAppKey: string;
  } | null>(null);

  const { toast } = useToast();

  const [commitDeleteIntegration] = useMutation<DatadogIntegrationsDialogDeleteIntegrationMutation>(
    deleteIntegrationMutation
  );

  const [commitUpdateDescription] = useMutation<DatadogIntegrationsDialogUpdateDescriptionMutation>(
    updateDescriptionMutation
  );

  const data = useLazyLoadQuery<DatadogIntegrationsDialogQuery>(
    datadogIntegrationsDialogQuery,
    {},
    { fetchPolicy: "network-only" },
  );

  const datadogIntegrations = data.me.organization?.integrations?.edges || [];

  const handleConnectAnother = () => {
    setIsConnectingNew(true);
  };

  const handleEditDescriptor = (integrationId: string, integrationName: string, currentDescription: string | null) => {
    setEditingDescriptorDialog({
      isOpen: true,
      integrationId,
      integrationName,
      currentValue: currentDescription || "",
    });
  };

  const handleEditKeys = (integrationId: string, integrationName: string, datadogSite: string, redactedApiKey: string, redactedAppKey: string) => {
    setEditingKeysDialog({ isOpen: true, integrationId, integrationName, datadogSite, redactedApiKey, redactedAppKey });
  };

  const handleSaveDescriptor = () => {
    if (!editingDescriptorDialog.integrationId) return;

    setIsSavingDescriptor(true);
    commitUpdateDescription({
      variables: {
        input: {
          integrationId: editingDescriptorDialog.integrationId,
          description: editingDescriptorDialog.currentValue || null,
        },
      },
      onCompleted: (response, errors) => {
        setIsSavingDescriptor(false);

        if (errors && errors.length > 0) {
          toast({
            title: "Error updating agent descriptor",
            description: errors.map(e => e.message).join(", "),
            variant: "error",
          });
          return;
        }

        if (response.updateIntegrationDescription.error) {
          toast({
            title: "Error updating agent descriptor",
            description: response.updateIntegrationDescription.error,
            variant: "error",
          });
          return;
        }

        toast({
          title: "Agent descriptor updated",
          variant: "success",
        });

        setEditingDescriptorDialog({
          isOpen: false,
          integrationId: null,
          integrationName: null,
          currentValue: "",
        });

        if (onRefetch) {
          onRefetch();
        }
      },
      onError: (error) => {
        setIsSavingDescriptor(false);
        toast({
          title: "Error updating agent descriptor",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleCancelEditDescriptor = () => {
    setEditingDescriptorDialog({
      isOpen: false,
      integrationId: null,
      integrationName: null,
      currentValue: "",
    });
  };

  const handleDeleteClick = (integration: any) => {
    setDeleteConfirmDialog({
      isOpen: true,
      integration,
    });
    setDeleteError(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmDialog.integration) {
      setDeleteError("Integration not found");
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    commitDeleteIntegration({
      variables: {
        input: {
          id: deleteConfirmDialog.integration.id,
        },
      },
      onCompleted: (response, errors) => {
        setIsDeleting(false);

        if (errors && errors.length > 0) {
          setDeleteError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setDeleteError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setDeleteConfirmDialog({ isOpen: false, integration: null });
          toast({
            title: "Datadog integration disconnected",
            variant: "success",
          });

          // Close dialog if no more integrations
          if (datadogIntegrations.length <= 1) {
            onClose();
          }

          onRefetch?.();
        }
      },
      onError: (error) => {
        setIsDeleting(false);
        setDeleteError(error.message);
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ isOpen: false, integration: null });
    setDeleteError(null);
  };

  // If no integrations, show the full Datadog connection flow
  if (datadogIntegrations.length === 0 || isConnectingNew) {
    return (
      <DatadogIntegration
        isOpen={isOpen}
        onClose={() => {
          setIsConnectingNew(false);
          onClose();
        }}
        onRefetch={onRefetch}
        brandTheme={brandTheme}
      />
    );
  }

  // Otherwise, show the integrations management dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <DatadogIcon className="h-6 w-6" />
            Datadog Integrations
          </DialogTitle>
          <DialogDescription>
            Manage your Datadog integrations. You can connect multiple Datadog organizations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto overflow-x-hidden max-h-[80vh] p-1">
          {/* Integrations Table */}
          <div className="space-y-4 overflow-x-auto">
            <h3 className="text-lg font-medium text-foreground">Connected Integrations</h3>

            {/* Header row */}
            <div className="grid grid-cols-[minmax(120px,1.5fr)_minmax(120px,1.2fr)_minmax(140px,1.3fr)_minmax(100px,1fr)_minmax(150px,2fr)_40px] gap-6 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground">Integration Name</div>
              <div className="text-sm font-medium text-muted-foreground">Site</div>
              <div className="text-sm font-medium text-muted-foreground">Subdomain</div>
              <div className="text-sm font-medium text-muted-foreground">Connected</div>
              <div className="text-sm font-medium text-muted-foreground">Agent Descriptor</div>
              <div></div>
            </div>

            {/* Integration rows */}
            {datadogIntegrations.length > 0 && (
              datadogIntegrations.map((edge): React.ReactNode => {
                const integration = edge.node;
                const credentials = integration.credentials as any;
                const integrationName = integration.name || "Unknown";
                const orgSubdomain = credentials?.org_subdomain || "N/A";
                const datadogSite = credentials?.datadog_site || "N/A";
                const createdAt = integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : "N/A";

                return (
                  <div key={integration.id} className="grid grid-cols-[minmax(120px,1.5fr)_minmax(120px,1.2fr)_minmax(140px,1.3fr)_minmax(100px,1fr)_minmax(150px,2fr)_40px] gap-6 items-start py-2">
                    <div className="text-foreground font-medium pt-0.5 truncate">{integrationName}</div>
                    <div className="text-muted-foreground text-sm pt-0.5 min-w-0">{datadogSite}</div>
                    <div className="text-muted-foreground text-sm pt-0.5 min-w-0 truncate">{orgSubdomain}</div>
                    <div className="text-muted-foreground text-sm pt-0.5 min-w-0">{createdAt}</div>
                    <div className="text-muted-foreground text-sm pt-0.5 min-w-0 truncate">
                      {integration.description || "No descriptor"}
                    </div>
                    <div className="flex gap-2 items-start pt-0.5 justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <IconButton
                            icon={MoreVertical}
                            variant="none"
                            size="sm"
                            className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                            title="More options"
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-card border-border text-foreground">
                          <div>
                            <Tooltip
                              content={!canModify ? "You don't have permission to edit integrations" : null}
                              position="left"
                            >
                              <DropdownMenuItem
                                onClick={() => canModify && handleEditDescriptor(integration.id, integrationName, integration.description || null)}
                                className="hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!canModify}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Agent Descriptor
                              </DropdownMenuItem>
                            </Tooltip>
                          </div>
                          <div>
                            <Tooltip
                              content={!canModify ? "You don't have permission to edit integrations" : null}
                              position="left"
                            >
                              <DropdownMenuItem
                                onClick={() => canModify && handleEditKeys(integration.id, integrationName, datadogSite, credentials?.api_key || "", credentials?.app_key || "")}
                                className="hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!canModify}
                              >
                                <KeyRound className="h-4 w-4 mr-2" />
                                Edit Keys
                              </DropdownMenuItem>
                            </Tooltip>
                          </div>
                          <div>
                            <Tooltip
                              content={!canModify ? "You don't have permission to disconnect integrations" : null}
                              position="left"
                            >
                              <DropdownMenuItem
                                onClick={() => canModify && handleDeleteClick(integration)}
                                className="hover:bg-accent cursor-pointer text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!canModify}
                              >
                                <Trash className="h-4 w-4 mr-2" />
                                Disconnect
                              </DropdownMenuItem>
                            </Tooltip>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })
            )}

            {/* Action buttons section */}
            <div className="pt-4 border-t border-accent space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Connect additional Datadog organizations to {productName}.
                </p>
                <Tooltip
                  content={!canModify ? "You don't have permission to connect integrations" : null}
                  position="top"
                >
                  <span className="inline-block">
                    <Button
                      onClick={handleConnectAnother}
                      disabled={isConnectingNew || !canModify}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {isConnectingNew ? "Loading..." : "Connect Another Organization"}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Edit descriptor dialog */}
      <Dialog open={editingDescriptorDialog.isOpen} onOpenChange={(open) => !open && handleCancelEditDescriptor()}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] bg-popover text-foreground border-border flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Agent Descriptor</DialogTitle>
            <DialogDescription>
              Edit the agent descriptor for {editingDescriptorDialog.integrationName} to help distinguish between multiple integrations.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 py-4">
            <MarkdownEditor
              value={editingDescriptorDialog.currentValue}
              onChange={(value) => setEditingDescriptorDialog(prev => ({ ...prev, currentValue: value }))}
              height="400px"
              placeholder="Describe how the AI agent should use this Datadog integration if multiple datadog integrations are connected..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-accent">
            <Button
              onClick={handleCancelEditDescriptor}
              variant="outline"
              disabled={isSavingDescriptor}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDescriptor}
              disabled={isSavingDescriptor}
            >
              {isSavingDescriptor ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <UIConfirmationDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Disconnect Datadog Integration"
        confirmText={isDeleting ? "Disconnecting..." : "Disconnect"}
        cancelText="Cancel"
        variant="destructive"
        confirmDisabled={isDeleting}
      >
        <div className="space-y-3">
          <p>
            Are you sure you want to disconnect the Datadog integration{" "}
            <span className="font-semibold">
              {deleteConfirmDialog.integration?.name || "this integration"}
            </span>
            ?
          </p>
          <p className="text-sm text-muted-foreground">
            This will remove access to all data from this Datadog integration. This action cannot be undone.
          </p>

          {deleteError && (
            <ErrorAlert icon="none">
              {deleteError}
            </ErrorAlert>
          )}
        </div>
      </UIConfirmationDialog>

      {/* Edit keys dialog */}
      <Dialog
        open={!!editingKeysDialog?.isOpen}
        onOpenChange={(open) => !open && setEditingKeysDialog(null)}
      >
        <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <DatadogIcon className="h-6 w-6" />
              Edit Keys — {editingKeysDialog?.integrationName}
            </DialogTitle>
            <DialogDescription>
              Update the API key and/or application key. Leave a field blank to keep the current key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
            {editingKeysDialog && (
              <DatadogIntegrationForm
                onComplete={(success) => {
                  if (success) {
                    setEditingKeysDialog(null);
                    toast({ title: "Keys updated", variant: "success" });
                    onRefetch?.();
                  }
                }}
                onCancel={() => setEditingKeysDialog(null)}
                editMode={{
                  integrationId: editingKeysDialog.integrationId,
                  datadogSite: editingKeysDialog.datadogSite,
                  redactedApiKey: editingKeysDialog.redactedApiKey,
                  redactedAppKey: editingKeysDialog.redactedAppKey,
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
